import { push, replace } from "connected-react-router";
import * as comexMiddleware from "middleware/comex";
import { types as comexTypes } from "reducers/comex";
import { call, put, takeLatest } from "redux-saga/effects";
import * as i18n from "util/i18n";
import { credentialsErrorMessage } from "util/validateMessage";
import { actions as notificationActions } from "reducers/notification";
import { selectors as sessionSelectors } from "reducers/session";
import { store } from "store";
import { download } from "util/download";

const sagas = [
    takeLatest(comexTypes.REQUEST_GET_ACTIVE_COUNTRY_LIST, getActiveCountriesList),
    takeLatest(comexTypes.REQUEST_GET_COMEX_SELECTORS, getComexSelectorsList),
    takeLatest(comexTypes.CREATE_COMEX_CONTACT_REQUEST, createComexContact),
    takeLatest(comexTypes.GET_COMEX_CONTACT_LIST_REQUEST, getComexContactList),
    takeLatest(comexTypes.MODIFY_COMEX_CONTACT_PRE_REQUEST, modifyComexContactPre),
    takeLatest(comexTypes.MODIFY_COMEX_CONTACT_REQUEST, modifyComexContact),
    takeLatest(comexTypes.DELETE_COMEX_CONTACT_REQUEST, deleteComexContact),
    takeLatest(comexTypes.CREATE_COMEX_TRANSFER_STEP3_REQUEST, createComexStep3),
    takeLatest(comexTypes.DOCUMENT_UPLOAD_REQUEST, documentUpload),
    takeLatest(comexTypes.GENERATE_TRANSFER_REQUEST, generatTransfer),
    takeLatest(comexTypes.DOCUMENT_DELETE_REQUEST, documentDelete),
    takeLatest(comexTypes.DOCUMENT_DOWNLOAD_REQUEST, documentDownload),
    takeLatest(comexTypes.GET_TRANSACTIONS_LIST_REQUEST, getTransactionsList),
    takeLatest(comexTypes.GET_DRAFT_TRANSACTIONS_LIST_REQUEST, getTransactionsList),
    takeLatest(comexTypes.GET_FINISHED_TRANSACTIONS_LIST_REQUEST, getTransactionsList),
    takeLatest(comexTypes.SEND_TRANSACTION_TICKET_REQUEST, sendTransactionTicket),
];

export default sagas;

function* getActiveCountriesList() {
    const response = yield call(comexMiddleware.getActiveCountries);

    if (response) {
        if (response.type === "W") {
            yield put({
                type: comexTypes.FAILURE_GET_ACTIVE_COUNTRY_LIST,
                errors: response.data.data,
            });
        } else {
            const { countrySelector } = response.data.data;
            yield put({
                type: comexTypes.SUCCESS_GET_ACTIVE_COUNTRY_LIST,
                countryList: countrySelector,
            });
        }
    }
}

function* getComexSelectorsList() {
    const response = yield call(comexMiddleware.getComexSelectorsList);

    if (response) {
        if (response.type === "W") {
            yield put({
                type: comexTypes.FAILURE_GET_COMEX_SELECTORS,
                errors: response.data.data,
            });
        } else {
            const { currencies, countries } = response.data.data;
            yield put({
                type: comexTypes.SUCCESS_GET_COMEX_SELECTORS,
                currencies,
                countries,
            });
        }
    }
}

function* createComexContact({ comexContact, formikBag, credentialType }) {
    try {
        const response = yield call(comexMiddleware.createComexContact, comexContact);
        if (response.type === "W") {
            let message = credentialsErrorMessage(response.data?.data);

            if (response.data?.data?.password || response.data?.data?.otp) {
                message =
                    credentialType === "password"
                        ? i18n.get("preferences.password.password.incorrect")
                        : i18n.get("login.step1.secondFactor.invalid");

                formikBag.setErrors({
                    credential: message,
                });

                yield put(notificationActions.showNotification(message, "error", ["ComexPage"]));

                return;
            }

            yield put(notificationActions.showNotification(response.data?.message, "error", ["ComexPage"]));
        } else {
            yield put({
                type: comexTypes.CREATE_COMEX_CONTACT_SUCCESS,
            });

            yield put(
                notificationActions.showNotification(i18n.get("comex.addContact.success.message"), "success", [
                    "Step1Comex",
                ]),
            );

            yield put(push("/comex/concept"));
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* getComexContactList({ filters = "", pageNo }) {
    const { id: idEnvironment } = sessionSelectors.getActiveEnvironment(store.getState());
    const response = yield call(comexMiddleware.getComexContactList, {
        nameSearch: filters,
        pageNumber: pageNo,
    });
    if (response.type === "W") {
        yield put(comexTypes.GET_COMEX_CONTACT_LIST_FAILURE);
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["FrequentDestinations"]),
        );
    } else {
        yield put({
            type: comexTypes.GET_COMEX_CONTACT_LIST_SUCCESS,
            idEnvironment,
            comexContactList: response.data.data.contactDestinations,
            moreStatements: response.data.data.moreStatements,
            pageNumber: response.data.data.pageNumber,
            totalPages: response.data.data.totalPages,
        });
    }
}

function* modifyComexContactPre({ idContactDestination }) {
    const response = yield call(comexMiddleware.modifyComexContactPre, { idContactDestination });
    if (response.type === "W") {
        yield put(comexTypes.MODIFY_COMEX_CONTACT_PRE_FAILURE);
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["CreateComexContact"]),
        );
    } else {
        yield put({
            type: comexTypes.MODIFY_COMEX_CONTACT_PRE_SUCCESS,
            comexContact: response.data.data.contactDestination,
        });
    }
}

function* modifyComexContact({ comexContact, formikBag, credentialType }) {
    try {
        const response = yield call(comexMiddleware.modifyComexContact, comexContact);
        if (response.type === "W") {
            let message = credentialsErrorMessage(response.data?.data);

            if (response.data?.data?.password || response.data?.data?.otp) {
                message =
                    credentialType === "password"
                        ? i18n.get("preferences.password.password.incorrect")
                        : i18n.get("login.step1.secondFactor.invalid");

                formikBag.setErrors({
                    credential: message,
                });

                yield put(notificationActions.showNotification(message, "error", ["ComexPage"]));

                return;
            }

            yield put(notificationActions.showNotification(response.data?.message, "error", ["ComexPage"]));
        } else {
            yield put({
                type: comexTypes.CREATE_COMEX_CONTACT_SUCCESS,
            });

            yield put(
                notificationActions.showNotification(i18n.get("comex.editContact.success.message"), "success", [
                    "contactDestinationsPage",
                ]),
            );

            yield put(push("/comex/contact"));
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* deleteComexContact({ params }) {
    const response = yield call(comexMiddleware.deleteComexContact, params);
    if (response.type === "W") {
        yield put({ type: comexTypes.DELETE_COMEX_CONTACT_FAILURE });

        yield put(
            notificationActions.showNotification("<strong>Error.</strong> ".concat(response.data.message), "error", [
                "contactDestinationsPage",
            ]),
        );
    } else {
        yield put({
            type: comexTypes.DELETE_COMEX_CONTACT_SUCCESS,
        });
        yield put(
            notificationActions.showNotification(i18n.get("comex.contact.delete.success"), "success", [
                "contactDestinationsPage",
            ]),
        );
    }
}

function* createComexStep3({ comexTransfer }) {
    const response = yield call(comexMiddleware.createComexStep3, comexTransfer);
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data?.message, "error", ["ComexNotification"]));
    } else {
        yield put({
            type: comexTypes.CREATE_COMEX_TRANSFER_STEP3_SUCCESS,
            comexId: response.data?.data?.comexId,
        });
        
        // Actualizo el listado de borraodres
        yield put({
            type: comexTypes.GET_DRAFT_TRANSACTIONS_LIST_REQUEST,
            params: { queryType: "D" }
        });

        // Si se presiono continuar, no muestro el snackbar
        if (!comexTransfer.isContinue) {
            yield put(notificationActions.showNotification(response.data?.message, "success", ["ComexNotification"]));
        }
    }
}

function* documentUpload({ documentToUpload }) {
    try {
        const params = {
            documentNumber: documentToUpload.documentNumber,
            documentType: documentToUpload.documentType,
            documentCode: documentToUpload.documentCode,
            documentName: documentToUpload.documentName,
            _documentToSave: documentToUpload._documentToSave,
            step: documentToUpload.step,
            comexId: documentToUpload.comexId,
        };
        const response = yield call(comexMiddleware.documentUpload, params);

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: comexTypes.DOCUMENT_UPLOAD_SUCCESS,
                documentToUploadRequest: response.data?.data,
                documentCode: documentToUpload.uploaderRequestId,
            });
        } else {
            yield put({
                type: comexTypes.DOCUMENT_UPLOAD_FAILURE,
                documentToUploadRequest: response?.data?.data?.message || response?.data?.message,
                documentCode: documentToUpload.uploaderRequestId,
            });
        }
    } catch (error) {
        yield put({
            type: comexTypes.DOCUMENT_UPLOAD_FAILURE,
            documentToUploadRequest: error?.response?.data?.message || error?.data?.message,
            documentCode: documentToUpload.uploaderRequestId,
        });
    }
}

function* generatTransfer({ comexTransfer, formikBag, credentialType }) {
    try {
        const response = yield call(comexMiddleware.generateComexTransfer, comexTransfer);
        if (response.type === "W") {
            let message = credentialsErrorMessage(response.data?.data);

            if (response.data?.data?.password || response.data?.data?.otp) {
                message =
                    credentialType === "password"
                        ? i18n.get("preferences.password.password.incorrect")
                        : i18n.get("login.step1.secondFactor.invalid");

                formikBag.setErrors({
                    credential: message,
                });

                yield put(notificationActions.showNotification(message, "error", ["ComexTransferSummaryPage"]));

                return;
            }

            yield put(
                notificationActions.showNotification(i18n.get("generate.comex.general.error"), "error", [
                    "ComexTransferSummaryPage",
                ]),
            );
        } else {
            yield put({
                type: comexTypes.GENERATE_TRANSFER_SUCCESS,
            });
            yield put({
                type: comexTypes.GET_TRANSACTIONS_LIST_REQUEST,
                params: { queryType: "S" }
            });
            yield put({
                type: comexTypes.GET_DRAFT_TRANSACTIONS_LIST_REQUEST,
                params: { queryType: "D" }
            });
            yield put(replace(`/comex/transaction/${response.data.idTransaction}`));
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* documentDelete({ documentId }) {
    try {
        const params = {
            documentId,
        };
        const response = yield call(comexMiddleware.documentDelete, params);

        if (response && response.data?.code === "COR000I") {
            yield put({
                type: comexTypes.DOCUMENT_DELETE_SUCCESS,
            });
        } else {
            yield put({
                type: comexTypes.DOCUMENT_DELETE_FAILURE,
            });
        }
    } catch (error) {
        yield put({
            type: comexTypes.DOCUMENT_DELETE_FAILURE,
        });
    }
}

function* documentDownload({ comexId, documentCode }) {
    const { data, type } = yield call(comexMiddleware.downloadFileComex, { comexId, documentCode });
    if (type === "W") {
        yield put({ type: comexTypes.DOCUMENT_DOWNLOAD_FAILURE });
    } else {
        const { content, fileName } = data.data;
        download(fileName, content);
        yield put({ type: comexTypes.DOCUMENT_DOWNLOAD_SUCCESS });
    }
}

function* getTransactionsList({ params }) {
    const { data, type } = yield call(comexMiddleware.getTransactionsList, params);
    const queryType = params.queryType;
    if (type === "W") {
        yield put(notificationActions.showNotification(i18n.get("comexTransactions.query.error"), "error", ["comexTransactions"]));
        yield put({ type: queryType == "S" ? comexTypes.GET_TRANSACTIONS_LIST_FAILURE : queryType == "F" ? comexTypes.GET_FINISHED_TRANSACTIONS_LIST_FAILURE: comexTypes.GET_DRAFT_TRANSACTIONS_LIST_FAILURE});
    } else {
        let { transactionsList } = data.data;        
        transactionsList = transactionsList && transactionsList.length == 0 ? null : transactionsList;
        if (queryType == "S")
            yield put({ type: comexTypes.GET_TRANSACTIONS_LIST_SUCCESS, transactionsList });
        if (queryType == "D")
            yield put({ type: comexTypes.GET_DRAFT_TRANSACTIONS_LIST_SUCCESS, draftTransactionsList : transactionsList });
        if (queryType == "F")
            yield put({ type: comexTypes.GET_FINISHED_TRANSACTIONS_LIST_SUCCESS, finishedTransactionsList : transactionsList });
    } 
}

function* sendTransactionTicket({ request }) {
    const { toAddress, body, subject } = request;
    const transaction_id = request.transactionId;
    const response = yield call(comexMiddleware.sendTransactionTicket, { transaction_id, toAddress, body, subject });

    if (response && response.status === 200) {
        yield put({
            type: comexTypes.SEND_TRANSACTION_TICKET_SUCCESS,
        });
        yield put(
            notificationActions.showNotification(i18n.get("comex.ticket.send.success"), "success", [
                "comexTransactions",
            ]),
        );
    } else {
        yield put({
            type: comexTypes.SEND_TRANSACTION_TICKET_FAILURE,
        });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["comexTransactions"]));
    }
}
